/**
 * Contact-list member shards, typed for the client SDK.
 *
 * Shard layout, sizing, and assignment live in
 * `../documents/emailContactList`; this module only binds them to
 * `firebase/firestore` refs. See that module for why members are sharded at
 * all.
 *
 * @module
 */
import type {
  CollectionReference,
  DocumentReference,
  Firestore,
} from 'firebase/firestore';
import {collection, doc, getFirestore} from 'firebase/firestore';
import {z} from 'zod';

import {
  ContactListMemberShardDocumentSchema,
  getContactListMemberShardCollectionPath,
  getContactListMemberShardDocumentPath,
} from '../documents/emailContactList';
import {ClientTimestampSchema, createConverter} from './sdk';

// ============================================================================
// SDK-specific schema
// ============================================================================

/** {@link ContactListMemberShardDocumentSchema} with client `Timestamp`s. */
export const ContactListMemberShardSchema =
  ContactListMemberShardDocumentSchema.extend({
    updatedAt: ClientTimestampSchema,
  }).loose();

/** Inferred type for {@link ContactListMemberShardSchema}. */
export type ContactListMemberShard = z.infer<
  typeof ContactListMemberShardSchema
>;

// ============================================================================
// Document refs
// ============================================================================

const contactListMemberShardConverter = createConverter({
  schema: ContactListMemberShardSchema,
  options: {validateOnRead: true, validateOnWrite: false},
});

/**
 * Ref to a contact list's member-shard collection.
 *
 * @param params - Lookup parameters.
 * @param params.courseId - Course owning the list.
 * @param params.listId - Firestore id of the list.
 * @param params.firestore - Firestore instance; defaults to the default app's.
 * @return The shard collection ref.
 * @throws Whatever `getFirestore()` throws when `firestore` is omitted and no
 *   default app has been initialized.
 */
export function getContactListMemberShardCollectionRef({
  courseId,
  listId,
  firestore = getFirestore(),
}: {
  courseId: string;
  listId: string;
  firestore?: Firestore;
}): CollectionReference<ContactListMemberShard> {
  return collection(
    firestore,
    getContactListMemberShardCollectionPath({courseId, listId}),
  ).withConverter(contactListMemberShardConverter);
}

/**
 * Ref to one member shard.
 *
 * @param params - Lookup parameters.
 * @param params.courseId - Course owning the list.
 * @param params.listId - Firestore id of the list.
 * @param params.shardIndex - Zero-based shard index.
 * @param params.firestore - Firestore instance; defaults to the default app's.
 * @return The shard document ref.
 * @throws Whatever `getFirestore()` throws when `firestore` is omitted and no
 *   default app has been initialized.
 */
export function getContactListMemberShardDocRef({
  courseId,
  listId,
  shardIndex,
  firestore = getFirestore(),
}: {
  courseId: string;
  listId: string;
  shardIndex: number;
  firestore?: Firestore;
}): DocumentReference<ContactListMemberShard> {
  return doc(
    firestore,
    getContactListMemberShardDocumentPath({courseId, listId, shardIndex}),
  ).withConverter(contactListMemberShardConverter);
}
